// The Debug: Graph tab's drawing: tracked dates in a band over one lane per agent, each
// card on its date (Timeline) or step (Flow), and an arrow from what has to happen first to
// what waits on it. See docs/Y2026-Q4/design-debug-graph.md.
import type { KeyboardEvent, ReactElement } from "react";

import {
  fromDay,
  type Graph,
  type GraphLink,
  type GraphNode,
  outOfOrder,
  toDay,
} from "../utils/dependencyGraph";
import {
  AXIS_H,
  CARD_H,
  CARD_W,
  DOT_X,
  type GraphLayout,
  type GraphView,
  LANE_LABEL_W,
} from "../utils/dependencyGraphLayout";
import {
  checkState,
  resolvingActions,
  shownDue,
  statusLabel,
} from "../utils/nextActions";
import styles from "./DependencyGraph.module.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TYPE_LETTER = { contact: "E", decision: "D", task: "T" } as const;
const TITLE_CHARS = 24;

const shortDay = (day: number): string => {
  const d = new Date(fromDay(day));
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};
const clip = (text: string): string =>
  text.length > TITLE_CHARS ? `${text.slice(0, TITLE_CHARS - 1)}…` : text;

/** A lane's colour, by its place among the agents drawn. */
const agentTone = (index: number): string =>
  styles[`agent${index % 6}`] ?? "";

interface CardText {
  tone: string[];
  letter: string;
  detail: string;
  /** Red text after the detail: "Blocking", "Nobody on it". */
  alarm: string;
  rank: string;
}

const checkCard = (node: GraphNode, graph: Graph): CardText => {
  const check = graph.items.checks.get(node.id)?.check;
  if (!check) return { tone: [], letter: "?", detail: "", alarm: "", rank: "" };
  const state = checkState(check, graph.items);
  const working = resolvingActions(graph.items, node.id);
  const when = node.day === null ? "no date" : `${node.inferred ? "after" : "by"} ${shortDay(node.day)}`;
  const forAction = check.basis === "action" ? "+A" : "";
  if (state === "met")
    return { tone: [styles.met], letter: "✓", detail: `Met ${node.day === null ? "" : shortDay(node.day)}`, alarm: "", rank: forAction };
  if (state === "nobody")
    return { tone: [styles.alarmed], letter: "!", detail: "", alarm: "Nobody on it", rank: forAction };
  if (state === "wait_ended")
    return { tone: [styles.alarmed], letter: "!", detail: "", alarm: "Wait ended", rank: forAction };
  if (state === "waiting")
    return { tone: [styles.waiting], letter: "-", detail: `No action · ${when}`, alarm: "", rank: forAction };
  return {
    tone: [styles.live, working.some((a) => a.status?.late) ? styles.late : ""],
    letter: String(working.length),
    detail: `${working.length} action${working.length === 1 ? "" : "s"} · ${when}`,
    alarm: working.some((a) => a.blocking) ? "Blocking" : "",
    rank: forAction,
  };
};

const actionCard = (node: GraphNode, graph: Graph): CardText => {
  const action = graph.items.actions.get(node.id)?.action;
  if (!action) return { tone: [], letter: "?", detail: "", alarm: "", rank: "" };
  const status = action.status?.status ?? "";
  const when = action.closed
    ? shortDay(toDay(action.closed.happened_on || action.closed.at))
    : shownDue(action).date
      ? shortDay(toDay(shownDue(action).date))
      : "no date";
  const tone = action.closed
    ? [styles.finished]
    : status === "waiting" || status === "planned"
      ? [styles.waiting]
      : status === "awaiting_reply"
        ? [styles.theirs]
        : [styles.live];
  if (action.status?.late) tone.push(styles.late);
  return {
    tone,
    letter: TYPE_LETTER[action.type],
    detail: `${statusLabel(action) || (action.closed ? "Closed" : "")} · ${when}`,
    alarm: action.blocking && !action.closed ? "Blocking" : "",
    rank: action.priority && !action.closed ? `#${action.priority}` : "",
  };
};

const linkPath = (link: GraphLink, layout: GraphLayout): string | null => {
  const card = (id: string) => layout.cards.get(id);
  const date = (id: string) => layout.dates.get(id);
  const out = (id: string): [number, number] | null => {
    const c = card(id);
    if (c) return [c.left + CARD_W, c.top + CARD_H / 2];
    const d = date(id);
    return d ? [d.x, d.y + 8] : null;
  };
  const into = (id: string): [number, number] | null => {
    const c = card(id);
    if (c) return [c.left, c.top + CARD_H / 2];
    const d = date(id);
    return d ? [d.x, d.y + 9] : null;
  };
  if (link.kind === "send") {
    const a = card(link.from);
    const b = card(link.to);
    if (!a || !b) return null;
    const [xa, ya, xb, yb] = [a.left + DOT_X, a.top + CARD_H / 2, b.left + DOT_X, b.top + CARD_H / 2];
    return `M${xa} ${ya + 9} C${xa - 26} ${ya + 20}, ${xb - 26} ${yb - 20}, ${xb} ${yb - 9}`;
  }
  const from = out(link.from);
  const to = into(link.to);
  if (!from || !to) return null;
  const [x1, y1] = from;
  const [x2, y2] = to;
  if (link.kind === "serves")
    return `M${x1} ${y1} C${x1 + 40} ${y1}, ${x2} ${y2 + 70}, ${x2} ${y2 + 2}`;
  if (date(link.from))
    return `M${x1} ${y1} C${x1} ${y1 + 60}, ${x2 - 50} ${y2}, ${x2 - 2} ${y2}`;
  const dx = Math.max(40, Math.abs(x2 - x1) / 2);
  return `M${x1} ${y1} C${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2 - 2} ${y2}`;
};

/** The midpoint of a cubic path written as linkPath writes it. */
const midpoint = (path: string): [number, number] => {
  const p = (path.match(/-?[\d.]+/g) ?? []).map(Number);
  return [
    (p[0] + 3 * p[2] + 3 * p[4] + p[6]) / 8,
    (p[1] + 3 * p[3] + 3 * p[5] + p[7]) / 8,
  ];
};

export const DependencyGraph = ({
  graph,
  layout,
  view,
  shown,
  links,
  agentIndex,
  agentLabel,
  selected,
  related,
  highlighted,
  preview,
  onSelect,
}: {
  graph: Graph;
  layout: GraphLayout;
  view: GraphView;
  shown: GraphNode[];
  links: GraphLink[];
  agentIndex: Map<string, number>;
  agentLabel: (agent: string) => string;
  selected: string | null;
  /** What the selection lights; null with nothing selected. */
  related: Set<string> | null;
  /** Cards drawn as selected: the selection, or a selected check's actions. */
  highlighted: Set<string>;
  preview: Map<string, "go" | "look"> | null;
  onSelect: (id: string | null) => void;
}) => {
  const lit = (id: string) => !related || related.has(id);
  const today = toDay(graph.today);
  const { width, height } = layout;
  const keySelect = (id: string) => (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(id === selected ? null : id);
    }
  };
  const tone = (agent: string) => agentTone(agentIndex.get(agent) ?? 0);

  const ticks: ReactElement[] = [];
  if (view === "timeline") {
    for (let day = layout.startDay; day <= layout.endDay; day++) {
      const d = new Date(fromDay(day));
      const x = layout.xOfDay(day);
      if (d.getUTCDate() === 1)
        ticks.push(
          <g key={day}>
            <line className={styles.tickMonth} x1={x} x2={x} y1={18} y2={height} />
            <text className={styles.tickMonthText} x={x + 4} y={14}>
              {MONTHS[d.getUTCMonth()]} {d.getUTCFullYear()}
            </text>
          </g>,
        );
      else if (d.getUTCDay() === 1 && (layout.pxPerDay >= 12 || d.getUTCDate() <= 7 || d.getUTCDate() % 14 < 7))
        ticks.push(
          <g key={day}>
            <line className={styles.tick} x1={x} x2={x} y1={22} y2={height} />
            <text className={styles.tickText} x={x + 3} y={30}>
              {d.getUTCDate()}
            </text>
          </g>,
        );
    }
  } else {
    for (let c = 0; c < layout.columns; c++) {
      const x = layout.xOfColumn(c) - 12;
      ticks.push(
        <g key={c}>
          <line className={styles.tick} x1={x - 8} x2={x - 8} y1={8} y2={height} />
          <text className={styles.columnHead} x={x} y={24}>
            {c === 0 ? "CAN START NOW" : `STEP ${c + 1}`}
          </text>
        </g>,
      );
    }
  }

  return (
    <svg
      className={`${styles.svg} ${related ? styles.focus : ""}`}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="group"
      aria-label="How the project's items depend on each other"
      onClick={(e) => {
        if (e.target === e.currentTarget) onSelect(null);
      }}
    >
      <defs>
        {(["arrow", "arrowCross", "arrowWrong", "arrowMuted"] as const).map((id) => (
          <marker key={id} id={`dg-${id}`} viewBox="0 0 10 10" refX={9} refY={5} markerWidth={7} markerHeight={7} orient="auto-start-reverse">
            <path d="M0 0L10 5L0 10z" className={styles[id]} />
          </marker>
        ))}
      </defs>

      {layout.lanes.map((lane, i) => (
        <g key={lane.agent} className={tone(lane.agent)}>
          <rect className={i % 2 ? styles.laneAlt : styles.lane} x={0} y={lane.top} width={width} height={lane.height} onClick={() => onSelect(null)} />
          <circle className={styles.laneDot} cx={16} cy={lane.top + 21} r={5} />
          <text className={styles.laneLabel} x={28} y={lane.top + 25}>
            {agentLabel(lane.agent)}
          </text>
        </g>
      ))}
      <rect className={styles.band} x={0} y={layout.bandTop} width={width} height={layout.bandHeight} onClick={() => onSelect(null)} />
      <text className={styles.bandLabel} x={16} y={layout.bandTop + 22}>
        TRACKED DATES
      </text>
      <line className={styles.rule} x1={0} x2={width} y1={layout.bandTop + layout.bandHeight} y2={layout.bandTop + layout.bandHeight} />
      {layout.gutter > 0 && (
        <>
          <rect className={styles.gutter} x={LANE_LABEL_W} y={layout.bandTop + layout.bandHeight} width={layout.gutter - 10} height={height - layout.bandTop - layout.bandHeight} />
          <text className={styles.columnHead} x={LANE_LABEL_W + 12} y={AXIS_H - 12}>
            NO DATE
          </text>
        </>
      )}
      {ticks}
      {view === "timeline" && (
        <g>
          <line className={styles.today} x1={layout.xOfDay(today)} x2={layout.xOfDay(today)} y1={20} y2={height} />
          <text className={styles.todayText} x={layout.xOfDay(today) + 4} y={30}>
            Today
          </text>
        </g>
      )}

      {[...layout.dates].map(([id, d]) => {
        const date = graph.items.dates.get(id)?.date;
        return (
          <line key={`guide-${id}`} className={`${styles.guide} ${date?.hardness === "hard" ? styles.guideHard : ""} ${lit(id) ? "" : styles.dim}`} x1={d.x} x2={d.x} y1={d.y + 8} y2={height} />
        );
      })}

      {links.map((link) => {
        const path = linkPath(link, layout);
        if (!path) return null;
        const wrong = outOfOrder(graph, link);
        const cross = graph.nodes.get(link.from)?.agent !== graph.nodes.get(link.to)?.agent;
        const on = !related || (related.has(link.from) && related.has(link.to));
        const kind = link.kind === "waits" ? (wrong ? styles.wrong : cross ? styles.cross : styles.waits) : link.kind === "serves" ? styles.serves : styles.send;
        const marker = link.kind === "send" ? undefined : link.kind === "serves" ? "arrowMuted" : wrong ? "arrowWrong" : cross ? "arrowCross" : "arrow";
        const [mx, my] = midpoint(path);
        const pill = link.condition ? link.condition.length * 5.4 + 12 : 0;
        return (
          <g key={`${link.kind}|${link.from}|${link.to}`} className={on ? "" : styles.dim}>
            <path className={`${styles.link} ${kind} ${link.satisfied ? styles.satisfied : ""}`} d={path} markerEnd={marker ? `url(#dg-${marker})` : undefined} />
            {link.condition && (
              <g className={styles.condition}>
                <rect x={mx - pill / 2} y={my - 8} width={pill} height={15} rx={7.5} />
                <text x={mx} y={my + 3} textAnchor="middle">
                  {link.condition}
                </text>
              </g>
            )}
          </g>
        );
      })}

      {[...layout.dates].map(([id, d]) => {
        const node = graph.nodes.get(id);
        const date = graph.items.dates.get(id)?.date;
        if (!node || !date) return null;
        return (
          <g key={id} className={`${styles.node} ${lit(id) ? "" : styles.dim} ${highlighted.has(id) ? styles.selected : ""}`} role="button" tabIndex={0} aria-label={`${date.what}, ${date.date || "no date"}`} aria-pressed={selected === id} onClick={() => onSelect(id === selected ? null : id)} onKeyDown={keySelect(id)}>
            <title>{date.what}</title>
            <rect x={d.x - 7} y={d.y - 7} width={14} height={14} transform={`rotate(45 ${d.x} ${d.y})`} className={date.hardness === "hard" ? styles.hard : styles.soft} />
            <text className={styles.dateLabel} x={d.x + 12} y={d.y - 1}>
              {clip(date.what)}
            </text>
            <text className={styles.dateSub} x={d.x + 12} y={d.y + 11}>
              {date.date ? shortDay(toDay(date.date)) : "no date"} · {date.hardness}
              {date.closed ? ` · ${date.closed.outcome}` : ""}
            </text>
          </g>
        );
      })}

      {shown
        .filter((n) => n.kind !== "date")
        .map((node, i) => {
          const at = layout.cards.get(node.id);
          if (!at) return null;
          const text = node.kind === "check" ? checkCard(node, graph) : actionCard(node, graph);
          const outcome = preview?.get(node.id);
          const classes = [
            styles.node,
            tone(node.agent),
            ...text.tone,
            lit(node.id) ? "" : styles.dim,
            highlighted.has(node.id) ? styles.selected : "",
            outcome === "go" ? styles.wouldGo : outcome === "look" ? styles.wouldLook : "",
            node.finished && node.kind === "action" ? styles.finished : "",
          ];
          const clipId = `dg-clip-${i}`;
          return (
            <g key={node.id} className={classes.join(" ")} role="button" tabIndex={0} aria-label={node.title} aria-pressed={selected === node.id} onClick={() => onSelect(node.id === selected ? null : node.id)} onKeyDown={keySelect(node.id)}>
              <title>{node.title}</title>
              <clipPath id={clipId}>
                <rect x={at.left} y={at.top} width={CARD_W - (text.rank ? 30 : 6)} height={CARD_H} />
              </clipPath>
              <rect className={styles.card} x={at.left} y={at.top} width={CARD_W} height={CARD_H} rx={6} />
              <circle className={styles.dot} cx={at.left + DOT_X} cy={at.top + CARD_H / 2} r={8} />
              <text className={styles.dotText} x={at.left + DOT_X} y={at.top + CARD_H / 2 + 3.3} textAnchor="middle">
                {text.letter}
              </text>
              <g clipPath={`url(#${clipId})`}>
                <text className={styles.cardTitle} x={at.left + 27} y={at.top + 16}>
                  {clip(node.title)}
                </text>
                <text className={styles.cardDetail} x={at.left + 27} y={at.top + 30}>
                  {text.detail}
                  {text.alarm && (
                    <tspan className={styles.alarm}>
                      {text.detail ? " · " : ""}
                      {text.alarm}
                    </tspan>
                  )}
                </text>
              </g>
              {text.rank && (
                <text className={styles.rank} x={at.left + CARD_W - 6} y={at.top + 30} textAnchor="end">
                  {text.rank}
                </text>
              )}
            </g>
          );
        })}
    </svg>
  );
};
